import React from 'react';
import moment from 'moment-timezone';
import { Col, Row, Tooltip } from 'antd';
import ProgressBar from '../../../../shared-components/ProgressBar';
import DatePickerFilter from './DatePickerFilter';
import NameFilter from './NameFilter';
import NumberRangeFilter from './NumberRangeFilter';
import styles from './home.module.sass';
import Icon from '@ant-design/icons';
import { ReactComponent as FilterFunnelSVG } from '../../../../assets/icons/FilterFunnelIcon.svg';
import CustomSearchFilter from './CustomSearchFilter';
import { ColumnTitle } from '../../../../shared-components/ColumnTitle';
import ScoreBadge from '../../../../shared-components/ScoreBadge';

export const columns = ({
  props,
  status,
  filters,
  sorting,
  sortOrder,
  filterSettings,
  checkedVendorsFilter,
  setCheckedVendorsFilter,
  setClearVendorInputFilter,
  checkedFeedsFilter,
  setCheckedFeedsFilter,
  filteredFeedSources,
  setClearFeedInputFilter,
  handleDateFilterSubmit,
  handleDateFilterReset,
  handleNameFilterSubmit,
  handleNameFilterReset,
  handleNumberRangeSubmit,
  handleNumberRangeReset,
  handleFeedFilterSubmit,
  handleFeedFilterReset,
  handleVendorFilterSubmit,
  handleVendorFilterReset,
}) => {
  const defaultSortOrderFeed = (sorting) => {
    switch (sorting.totalSourceScore) {
      case '-total_source_score':
        return 'descend';
      case 'total_source_score':
        return 'ascend';
      default:
        return null;
    }
  };
  const defaultSortOrderIoc = (sorting) => {
    switch (sorting.totalIoc) {
      case '-total_ioc':
        return 'descend';
      case 'total_ioc':
        return 'ascend';
      default:
        return null;
    }
  };
  const defaultSortOrderCustom = (sorting) => {
    switch (sorting.totalCustom) {
      case '-total_custom':
        return 'descend';
      case 'total_custom':
        return 'ascend';
      default:
        return null;
    }
  };
  const defaultSortOrderTotal = (sorting) => {
    switch (sorting.total) {
      case '-total':
        return 'descend';
      case 'total':
        return 'ascend';
      default:
        return null;
    }
  };
  const defaultSortOrderLastSeen = (sorting) => {
    switch (sorting.lastSeenOn) {
      case '-last_seen_on':
        return 'descend';
      case 'last_seen_on':
        return 'ascend';
      default:
        return null;
    }
  };

  let totalFeedTitle = 'Source Score';
  let totalIocTitle = 'Enriched Score';
  let totalCustomTitle = 'Custom Score';
  let totalTitle = 'Total Score';
  if (sortOrder.length > 1) {
    sortOrder.forEach((item, index) => {
      const sortItemName = item.replace('-', '');
      const priority = index + 1;
      if (sortItemName === 'total_source_score')
        totalFeedTitle += ` (${priority})`;
      if (sortItemName === 'total_ioc') totalIocTitle += ` (${priority})`;
      if (sortItemName === 'total_custom') totalCustomTitle += ` (${priority})`;
      if (sortItemName === 'total') totalTitle += ` (${priority})`;
    });
  }

  const textByType = {
    domain: 'Domain',
    ip: 'IP',
    url: 'URL',
  };

  const typeFilters = filterSettings?.types?.map((type) => ({
    value: type,
    text: textByType[type],
  }));
  const checkRangeFilter = (range) => {
    return (range && range[0] !== 0) || (range && range[1] !== 100)
      ? ['true']
      : null;
  };

  const FilterFunnelIcon = ({ filtered, ...props }) => {
    const iconStyle = {
      width: 24,
      height: 24,
    };
    return (
      <Icon
        {...props}
        component={() => (
          <FilterFunnelSVG
            style={iconStyle}
            fill={filtered ? '#2B7FEE' : 'white'}
          />
        )}
      />
    );
  };

  return [
    {
      title: (
        <ColumnTitle
          columnName="Threat Name"
          tooltipText="The name of the observable"
        />
      ),
      align: 'left',
      dataIndex: 'name',
      key: 'name',
      className: styles.threat_ranking_column,
      ellipsis: {
        showTitle: false,
      },
      render: (name) => (
        <Tooltip placement="topLeft" title={name}>
          {name}
        </Tooltip>
      ),

      filterDropdown: ({ confirm }) => (
        <NameFilter
          confirm={confirm}
          placeholder={'Search Observable Name'}
          filters={filters}
          onSubmit={handleNameFilterSubmit}
          onReset={handleNameFilterReset}
          status={status}
        />
      ),
      filteredValue: filters.name ? [filters.name] : null,
      //TODO: not sure if this is the easiest/recommend way of adding the tooltip to the icon
      filterIcon: (filtered) => (
        <Tooltip title="Filter by observable name. Tip: Use the clear button to clear your previous filter">
          <FilterFunnelIcon filtered={filtered} />
        </Tooltip>
      ),
    },
    {
      title: (
        <ColumnTitle
          columnName="IOC Type"
          tooltipText="The type of the observable can be IP address, domain, or url"
        />
      ),
      align: 'left',
      className: styles.ioc_type_column,
      dataIndex: 'type',
      key: 'type',
      width: 100,
      responsive: props?.collapsed ? ['xl'] : ['xxl'],
      defaultFilteredValue: [],
      filters: typeFilters,
      filteredValue: filters.type,
      filterIcon: (filtered) => (
        <Tooltip title="Filter by type (domain/IP/URL). Tip: Use the clear button to clear your previous filter">
          <FilterFunnelIcon filtered={filtered} />
        </Tooltip>
      ),
      render: (text) => {
        const typeChoices = {
          ip: 'IP',
          domain: 'Domain',
          url: 'URL',
        };
        return typeChoices[text.toLowerCase()];
      },
    },
    {
      title: (
        <ColumnTitle
          columnName="Source"
          tooltipText="The name of the feed providing the observable"
        />
      ),
      align: 'left',
      dataIndex: 'sourceName',
      key: 'sourceName',
      width: 200,
      className: styles.source_column_header,
      responsive: props?.collapsed ? ['xl'] : ['xxl'],
      defaultFilteredValue: [],
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <CustomSearchFilter
          filters={filteredFeedSources}
          handleEntryFilterSubmit={handleFeedFilterSubmit}
          handleEntryFilterReset={handleFeedFilterReset}
          checkedFilters={checkedFeedsFilter}
          setCheckedFilters={setCheckedFeedsFilter}
          confirm={confirm}
          clearFilters={clearFilters}
          setClearInputFilter={setClearFeedInputFilter}
          searchPlaceholderText={'Search Feed'}
        />
      ),
      filteredValue: filters.feed,
      filterIcon: (filtered) => (
        <Tooltip title="Filter by feed. Tip: Use the clear button to clear your previous filter">
          <FilterFunnelIcon filtered={filtered} />
        </Tooltip>
      ),
    },
    {
      title: (
        <ColumnTitle
          columnName="Vendor"
          tooltipText="The name of the owner of the feed"
        />
      ),
      align: 'left',
      dataIndex: 'sourceVendorName',
      key: 'sourceVendorName',
      //dataIndex: ['feeds', 'vendor'],
      //key: 'vendor',
      width: 100,
      className: styles.vendor_column_header,
      filterDropdown: ({ confirm, clearFilters }) => (
        <CustomSearchFilter
          filters={filterSettings.vendors}
          handleEntryFilterSubmit={handleVendorFilterSubmit}
          handleEntryFilterReset={handleVendorFilterReset}
          checkedFilters={checkedVendorsFilter}
          setCheckedFilters={setCheckedVendorsFilter}
          confirm={confirm}
          clearFilters={clearFilters}
          setClearInputFilter={setClearVendorInputFilter}
          searchPlaceholderText={'Search Vendor'}
        />
      ),
      filteredValue: filters.vendor,
      filterIcon: (filtered) => (
        <Tooltip title="Filter by vendor. Tip: Use the clear button to clear your previous filter">
          <FilterFunnelIcon filtered={filtered} />
        </Tooltip>
      ),
      responsive: props?.collapsed ? ['xl'] : ['xxl'],
    },
    /*{
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (text) => (
        // For POC purpose we just Randomize status to simulate
        // Items that are OFFLINE, ONLINE and WITH NO STATUS
        <StatusIcon status={_.sample([true, false, null])} />
      ),
    },*/
    // {
    //   title: (
    //     <ColumnTitle
    //       columnName="Last Seen"
    //       tooltipText="When the observable was last seen in any feeds"
    //     />
    //   ),
    //   align: 'left',
    //   dataIndex: 'lastSeenOn',
    //   key: 'lastSeenOn',
    //   responsive: props?.collapsed ? ['xl'] : ['xxl'],
    //   filterDropdown: ({ confirm }) => (
    //     <DatePickerFilter
    //       confirm={confirm}
    //       filters={filters}
    //       onSubmit={handleDateFilterSubmit}
    //       onReset={handleDateFilterReset}
    //       status={status}
    //     />
    //   ),
    //   className: styles.last_seen_column_header,
    //   filteredValue: filters.last_seen_from ? [filters.last_seen_from] : null,
    //   filterIcon: (filtered) => (
    //     <Tooltip title="Filter by last seen date. Tip: You can enter one date or range of dates.">
    //       <FilterFunnelIcon filtered={filtered} />
    //     </Tooltip>
    //   ),
    //   sorter: (a, b) => moment(a.date) - moment(b.date),
    //   sortDirections: ['descend', 'ascend'],
    //   sortOrder: defaultSortOrderLastSeen(sorting),
    //   render: (text) => {
    //     let datetime = 'No available timestamp';
    //     if (text !== null) {
    //       datetime = moment.utc(text).format('YYYY-MM-DD HH:mm').toString();
    //     }

    //     return datetime;
    //   },
    // },
    {
      title: (
        <ColumnTitle
          columnName={totalFeedTitle}
          tooltipText="Source harmfulness score from 0 to 100 calculated based on the Silent Push algorithm"
        />
      ),
      className: styles.source_score_column_header,
      align: 'left',
      dataIndex: 'totalSourceScore',
      key: 'totalSourceScore',
      sortDirections: ['descend', 'ascend'],
      sorter: {
        multiple: 4,
      },
      sortOrder: defaultSortOrderFeed(sorting),
      render: (text) => {
        let scoreColor = '';
        if (text < '25') {
          scoreColor = '#1f9110';
        } else if (text >= '25' && text <= '40') {
          scoreColor = '#f3be00';
        } else if (text > '40') {
          scoreColor = '#E84749';
        }
        return (
          <Row>
            <Col span={6}>
              <span className={styles['indicator_text']}>{text}</span>
            </Col>
            <Col
              offset={2}
              span={16}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              <ProgressBar bgcolor={scoreColor} completed={text} />
            </Col>
          </Row>
        );
      },
      filteredValue: checkRangeFilter(filters.totalFeedRange),
      filterIcon: (filtered) => (
        <Tooltip title="Filter by source score. Tip: Use the clear button to clear your previous filter">
          <FilterFunnelIcon filtered={filtered} />
        </Tooltip>
      ),
      filterDropdown: ({ confirm }) => (
        <NumberRangeFilter
          confirm={confirm}
          defaultValue={filters.totalFeedRange}
          onSubmit={(range) => handleNumberRangeSubmit('totalFeedRange', range)}
          onReset={() => handleNumberRangeReset('totalFeedRange')}
          limits={filterSettings.sourceScore}
          status={status}
        />
      ),
    },
    {
      title: (
        <ColumnTitle
          columnName={totalIocTitle}
          tooltipText="Cumulative score incorporating all secondary enriched attributes"
        />
      ),
      className: styles.enriched_score_column_header,
      align: 'left',
      dataIndex: 'totalIoc',
      key: 'totalIoc',
      sortDirections: ['descend', 'ascend'],
      sorter: {
        multiple: 3,
      },
      responsive: ['lg'],
      sortOrder: defaultSortOrderIoc(sorting),
      render: (text) => {
        let scoreColor = '';
        if (text < '25') {
          scoreColor = '#1f9110';
        } else if (text >= '25' && text <= '40') {
          scoreColor = '#f3be00';
        } else if (text > '40') {
          scoreColor = '#E84749';
        }
        return (
          <Row>
            <Col span={6}>
              <span className={styles['indicator_text']}>{text}</span>
            </Col>
            <Col
              offset={2}
              span={16}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              <ProgressBar bgcolor={scoreColor} completed={text} />
            </Col>
          </Row>
        );
      },
      filteredValue: checkRangeFilter(filters.totalIocRange),
      filterIcon: (filtered) => (
        <Tooltip title="Filter by secondary attributes score. Tip: Use the clear button to clear your previous filter">
          <FilterFunnelIcon filtered={filtered} />
        </Tooltip>
      ),
      filterDropdown: ({ confirm }) => (
        <NumberRangeFilter
          confirm={confirm}
          defaultValue={filters.totalIocRange}
          onSubmit={(range) => handleNumberRangeSubmit('totalIocRange', range)}
          onReset={() => handleNumberRangeReset('totalIocRange')}
          limits={filterSettings.customIndicatorsScore}
          status={status}
        />
      ),
    },
    {
      title: (
        <ColumnTitle
          columnName={totalCustomTitle}
          tooltipText="Cumulative score incorporating all custom attributes"
        />
      ),
      className: styles.custom_score_column_header,
      align: 'left',
      dataIndex: 'totalCustom',
      key: 'totalCustom',
      sortDirections: ['descend', 'ascend'],
      sorter: {
        multiple: 2,
      },
      responsive: ['lg'],
      sortOrder: defaultSortOrderCustom(sorting),
      render: (text) => {
        let scoreColor = '';
        if (text < '25') {
          scoreColor = '#1f9110';
        } else if (text >= '25' && text <= '40') {
          scoreColor = '#f3be00';
        } else if (text > '40') {
          scoreColor = '#E84749';
        }
        return (
          <Row>
            <Col span={6}>
              <span className={styles['indicator_text']}>{text}</span>
            </Col>
            <Col
              offset={2}
              span={16}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              <ProgressBar bgcolor={scoreColor} completed={text} />
            </Col>
          </Row>
          /*
          <div className="indicator_bar_and_text_container">
            <span className={styles["indicator_text"]}>{text}</span>
            <span className="indicator_progressbar">
              <ProgressBar bgcolor={scoreColor} completed={text} />
            </span>
          </div>
             */
        );
      },
      filteredValue: checkRangeFilter(filters.totalCustomRange),
      filterIcon: (filtered) => (
        <Tooltip title="Filter by custom attributes score. Tip: Use the clear button to clear your previous filter">
          <FilterFunnelIcon filtered={filtered} />
        </Tooltip>
      ),
      filterDropdown: ({ confirm }) => (
        <NumberRangeFilter
          confirm={confirm}
          defaultValue={filters.totalCustomRange}
          onSubmit={(range) =>
            handleNumberRangeSubmit('totalCustomRange', range)
          }
          onReset={() => handleNumberRangeReset('totalCustomRange')}
          limits={filterSettings.customIndicatorsScore}
          status={status}
        />
      ),
    },
    {
      title: (
        <ColumnTitle
          columnName={totalTitle}
          tooltipText="Single metric incorporating the source score, enriched score and source score."
        />
      ),
      sortDirections: ['descend', 'ascend'],
      dataIndex: 'total',
      key: 'total',
      sorter: {
        multiple: 1,
      },
      sortOrder: defaultSortOrderTotal(sorting),
      render: (text, record) => (
        <div style={{ margin: '2px auto' }}>
          <ScoreBadge
            score={+text}
            scoreTextRecolor={false}
            scoreProgressRecolor={false}
            boxBordered={false}
          />
        </div>
      ),
      filteredValue: checkRangeFilter(filters.totalRange),
      filterIcon: (filtered) => (
        <Tooltip title="Filter by total score. Tip: Use the clear button to clear your previous filter">
          <FilterFunnelIcon filtered={filtered} />
        </Tooltip>
      ),
      filterDropdown: ({ confirm }) => (
        <NumberRangeFilter
          confirm={confirm}
          defaultValue={filters.totalRange}
          onSubmit={(range) => handleNumberRangeSubmit('totalRange', range)}
          onReset={() => handleNumberRangeReset('totalRange')}
          limits={filterSettings.totalScore}
          status={status}
        />
      ),
    },
  ];
};
